import { PaymentMethodDTO } from 'api-models'
import { useState } from 'react'
import { RiBankCardLine, RiBankLine } from 'react-icons/ri'
import { GButton } from 'components/basic-blocks/g-button'
import { GListBox } from 'components/basic-blocks/g-listbox'
import { AddPaymentMethod } from 'components/add-payment-method'

// Controlled — parent owns the list + selection state.
export interface PaymentMethodPickerProps {
	paymentMethods: PaymentMethodDTO[]
	selectedId: string | undefined
	onChange: (pm: PaymentMethodDTO) => void
	onAdded: (pm: PaymentMethodDTO) => void
	disabled?: boolean
}

export const PaymentMethodPicker = ({
	paymentMethods,
	selectedId,
	onChange,
	onAdded,
	disabled
}: PaymentMethodPickerProps) => {
	const [adding, setAdding] = useState(false)

	const selected = paymentMethods.find((pm) => pm.id === selectedId) ?? null

	return (
		<div className="flex w-full flex-col gap-3">
			{paymentMethods.length > 0 && (
				<GListBox<PaymentMethodDTO>
					label="Payment method"
					options={paymentMethods}
					value={selected}
					containerClass="w-full"
					placeholder="Select payment method"
					onChange={(pm) => !disabled && onChange(pm)}
					renderLabel={(pm) => (
						<div>
							{pm.type === 'CARD' && (
								<div className="flex items-center space-x-2 text-sm">
									<RiBankCardLine className="h-4 w-4 text-primary-500" />
									<span className="font-medium">
										{pm.card?.brand?.toUpperCase()}{' '}
										<code className="font-normal text-t-secondary">
											*{pm.card?.last4}
										</code>
									</span>
								</div>
							)}
							{pm.type === 'DEBIT' && (
								<div className="flex items-center space-x-2 text-sm">
									<RiBankLine className="h-4 w-4 text-primary-500" />
									<span className="font-medium">
										Account{' '}
										<code className="font-normal text-t-secondary">
											#{pm.debit?.last4}
										</code>
									</span>
								</div>
							)}
						</div>
					)}
				/>
			)}

			<GButton
				label={paymentMethods.length > 0 ? 'Add another payment method' : 'Add payment method'}
				variant="text"
				color="neutral"
				className="h-[40px] w-full rounded-full border border-t-base-blue px-3 py-2 text-base text-t-base-blue shadow-none outline-none transition-all duration-200 hover:bg-t-base-blue hover:text-white"
				onClick={() => !disabled && setAdding(true)}
				disabled={disabled}
			/>

			<AddPaymentMethod
				open={adding}
				onClose={() => setAdding(false)}
				onFinish={(pm) => {
					setAdding(false)
					onAdded(pm)
				}}
			/>
		</div>
	)
}
